import cloneDeep from 'lodash/cloneDeep';
import State from '@/state/State';
import { TODO_ACTIONS } from '@/utils/Todo';
import useFeatureFlags from '@/composables/useFeatureFlags';
import ApiClient from '@/ApiClient';

export const NAME_REGEX = /^[a-zA-ZÀ-ÿ0-9 .,\-/&'‘’]+$/;

const MAX_SALARY = 500000;
const MAX_WAGERATE = 99.99;

const {
  showLeaversAsDisabled,
  canEnrollEmployees,
  canDisableEmployees,
  canTerminateEmployees,
  usingExternalIdAsEmployeeCode,
  hideEmployeeOptOut,
  hideEmployeePause,
} = useFeatureFlags();

export const SALARY_VALIDATION_RULES = {
  required: true,
  min_value: 0,
  max_value: MAX_SALARY,
};

export const WAGERATE_VALIDATION_RULES = {
  required: true,
  min_value: 0,
  max_value: MAX_WAGERATE,
};

export const EMPLOYEE_STATE = {
  NEW: 'NEW',
  INSTALLED: 'INSTALLED',
  PENDING: 'PENDING',
  ENROLLING: 'ENROLLING',
  ENROLLED: 'ENROLLED',
  ACTIVE: 'ACTIVE',
  OPTOUT: 'OPTED OUT',
  DISABLED: 'DISABLED',
  OPTOUTPENDING: 'OPT OUT PENDING',
};

export const EMPLOYEE_PAY_TYPE = {
  HOURLY: 'hourly',
  SALARY: 'salary',
};

const NEW_EMPLOYEE_STATE = [EMPLOYEE_STATE.NEW, EMPLOYEE_STATE.INSTALLED];

const PENDING_EMPLOYEE_STATE = [EMPLOYEE_STATE.PENDING];

const ENROLLED_EMPLOYEE_STATE = [EMPLOYEE_STATE.ENROLLING, EMPLOYEE_STATE.ENROLLED, EMPLOYEE_STATE.ACTIVE];

const DISABLED_EMPLOYEE_STATE = [EMPLOYEE_STATE.DISABLED, EMPLOYEE_STATE.OPTOUT, EMPLOYEE_STATE.OPTOUTPENDING];

export const isNewEmployee = employee => NEW_EMPLOYEE_STATE.includes(employee.current_state);

export const isPendingEmployee = employee => PENDING_EMPLOYEE_STATE.includes(employee.current_state);

export const isEnrolledEmployee = employee => ENROLLED_EMPLOYEE_STATE.includes(employee.current_state);

export const isDisabledOrOptedOutEmployee = employee => DISABLED_EMPLOYEE_STATE.includes(employee.current_state);

export const isEligibleEmployee = employee => !DISABLED_EMPLOYEE_STATE.includes(employee.current_state);

export const isSalariedEmployee = employee => employee.salary_properties.salary_or_hourly === EMPLOYEE_PAY_TYPE.SALARY;

export const canEnrollEmployee = employee => isPendingEmployee(employee) && canEnrollEmployees.value;

export const canOptOutEmployee = employee =>
  !hideEmployeeOptOut.value &&
  convertBackendStatusToDisplayStatus(employee) !== 'LEAVER' &&
  convertBackendStatusToDisplayStatus(employee) !== 'OPTED OUT' &&
  convertBackendStatusToDisplayStatus(employee) !== 'OPT OUT PENDING';

export const canCompleteOptOutForEmployee = employee =>
  canDisableEmployees.value &&
  !hideEmployeeOptOut.value &&
  convertBackendStatusToDisplayStatus(employee) == 'OPT OUT PENDING' &&
  State.state.todos.some(todo => todo.action === TODO_ACTIONS.DISABLE_EMPLOYEE && todo.fk === employee.employee_id);

export const canOptInEmployee = employee =>
  convertBackendStatusToDisplayStatus(employee) === 'OPTED OUT' ||
  (canEnrollEmployees.value && convertBackendStatusToDisplayStatus(employee) === 'LEAVER');

export const UNENROLMENT_SOURCE = {
  PARTNER_PORTAL: 'PARTNER_PORTAL',
};

export const canCompleteUnenrolmentForEmployee = employee =>
  canDisableEmployees.value &&
  !hideEmployeeOptOut.value &&
  (convertBackendStatusToDisplayStatus(employee) == 'LEAVER' ||
    employee.properties?.unenrolment_source === UNENROLMENT_SOURCE.PARTNER_PORTAL) &&
  State.state.todos.some(todo => todo.action === TODO_ACTIONS.DISABLE_EMPLOYEE && todo.fk === employee.employee_id);

// Matches the flag half of canCompleteUnenrolmentForEmployee, so the terminate page and the
// employee page agree on whether the completion action exists. The status and source half cannot be
// shared: the employee held by the terminate page is a pre-termination snapshot.
export const canCompleteBankingReturn = () => canDisableEmployees.value && !hideEmployeeOptOut.value;

// NOT_REQUIRED is a success: the employee is terminated and blocked from automatic reactivation,
// there was simply no beneficiary banking to push back to the client system.
export const PORTAL_UNENROLMENT_OUTCOME = {
  INITIATED: 'INITIATED',
  NOT_REQUIRED: 'NOT_REQUIRED',
};

export const TERMINATION_BLOCKED_MESSAGES = {
  alreadyDisabled: 'This employee is already disabled, so they cannot be terminated.',
  integrationSourced:
    'This employee was terminated via your integration and must be rehired via your integration. Contact support if you require assistance.',
  variationMissing:
    'Your company is not set up to terminate employees from the portal. Contact support if you require assistance.',
  notDisabled: 'This employee is no longer disabled, so there is no termination left to reverse.',
  alreadyTerminated: 'This employee has already been terminated. Rehire them from their profile instead.',
  notTerminated: 'This employee has not been terminated from the portal, so there is nothing to reverse.',
};

// Both endpoints require an admin-like role on top of the company variation, so an infostream
// user would only get a 404 from a button we offered them.
export const canAdminEmployeeFromPortal = () => Boolean(State.state.claims?.a || State.state.claims?.m);

// Held in one place because the route guards and getEmployeeTerminationStatus have to agree: a
// condition added to only one of them would leave a blocked page reachable by its URL.
export const canTerminateEmployeesAtAll = () => canTerminateEmployees.value && canAdminEmployeeFromPortal();

// Guards the banking-return pages, which would otherwise fall back to canAccessPortal and admit an
// infostream manager that every button leading here already withholds. Deliberately not gated on
// CAN_DISABLE_EMPLOYEES as well: the dashboard todo reaches this page without checking any flag, so
// adding one would turn an opt-out route companies use today into a not-found.
export const canCompleteBankingReturnAtAll = () => canAdminEmployeeFromPortal();

// The source is stamped on the termination whether or not any banking had to be unenrolled, and a
// failed reversion deliberately leaves it in place, so it is the only reliable signal that a portal
// termination still stands - the unenrolment properties are absent entirely on the strategies that
// never unenrol.
const isTerminatedViaPortal = employee =>
  employee?.properties?.unenrolment_source === UNENROLMENT_SOURCE.PARTNER_PORTAL;

// A termination from a payroll feed unenrols without recording a source, so it has to be undone in
// the integration.
const isUnenrolledViaIntegration = employee =>
  Boolean(employee?.properties?.unenrolment_initiated_at && !employee?.properties?.unenrolment_reverted_at);

// Shared by the Terminate/Rehire button and both pages so a blocked action can never be reached by
// navigating straight to the page URL. Undefined when the user cannot terminate at all.
export const getEmployeeTerminationStatus = employee => {
  if (!canTerminateEmployeesAtAll() || !employee) {
    return undefined;
  }

  if (isTerminatedViaPortal(employee)) {
    // reverse-unenrolment reactivates a disabled employee, so it rejects one who is already back.
    const reversible = employee.current_state === EMPLOYEE_STATE.DISABLED;
    return {
      action: 'REHIRE',
      isActionable: reversible,
      blockedReason: reversible ? undefined : TERMINATION_BLOCKED_MESSAGES.notDisabled,
    };
  }

  if (isUnenrolledViaIntegration(employee)) {
    return {
      action: 'REHIRE',
      isActionable: false,
      blockedReason: TERMINATION_BLOCKED_MESSAGES.integrationSourced,
    };
  }

  if (employee.current_state === EMPLOYEE_STATE.DISABLED) {
    return {
      action: 'TERMINATE',
      isActionable: false,
      blockedReason: TERMINATION_BLOCKED_MESSAGES.alreadyDisabled,
    };
  }

  return { action: 'TERMINATE', isActionable: true };
};

// Rehire is what reactivates a terminated employee, so an opt-in would not bring them back and the
// two buttons would offer the same outcome with only one of them working. An opted-out employee is
// unaffected: their status is TERMINATE rather than REHIRE, so their opt-in still stands.
export const isOptInSupersededByRehire = employee => getEmployeeTerminationStatus(employee)?.action === 'REHIRE';

// The pages need a message rather than the status the button switches on, and each has to reject an
// employee whose only available action is the other one. Undefined means the page may proceed.
// wrongAction resolves that case, and differs per page: a terminate-only employee is always simply
// not rehirable, whereas a rehire-only one may carry a more specific reason worth showing.
const getTerminationUnavailableReason = (employee, action, wrongAction) => {
  const status = getEmployeeTerminationStatus(employee);

  if (!status) {
    return TERMINATION_BLOCKED_MESSAGES.variationMissing;
  }

  if (status.action !== action) {
    return wrongAction(status);
  }

  return status.isActionable ? undefined : status.blockedReason;
};

export const getTerminateUnavailableReason = employee =>
  getTerminationUnavailableReason(
    employee,
    'TERMINATE',
    status => status.blockedReason || TERMINATION_BLOCKED_MESSAGES.alreadyTerminated,
  );

export const getRehireUnavailableReason = employee =>
  getTerminationUnavailableReason(employee, 'REHIRE', () => TERMINATION_BLOCKED_MESSAGES.notTerminated);

export const canPauseEmployee = employee =>
  !hideEmployeePause.value &&
  convertBackendStatusToDisplayStatus(employee) !== 'PAUSED' &&
  convertBackendStatusToDisplayStatus(employee) !== 'PAUSE SCHEDULED';

export const canUnpauseEmployee = employee =>
  !hideEmployeePause.value &&
  (convertBackendStatusToDisplayStatus(employee) === 'PAUSED' ||
    convertBackendStatusToDisplayStatus(employee) === 'PAUSE SCHEDULED');

export const canChangePasswordForEmployee = employee =>
  convertBackendStatusToDisplayStatus(employee) !== 'LEAVER' &&
  convertBackendStatusToDisplayStatus(employee) !== 'OPTED OUT' &&
  convertBackendStatusToDisplayStatus(employee) !== 'OPT OUT PENDING';

export const getStateCounts = enrolmentSnapshot => {
  const snapshot = {};
  snapshot['ELIGIBLE'] = State.state.company.properties.headcount || enrolmentSnapshot.eligible || 0;
  snapshot[EMPLOYEE_STATE.ENROLLING] = enrolmentSnapshot.enrolling + enrolmentSnapshot.pending || 0;
  snapshot[EMPLOYEE_STATE.ENROLLED] = enrolmentSnapshot.adopted || 0;
  snapshot[EMPLOYEE_STATE.NEW] =
    (State.state.company.properties.headcount
      ? State.state.company.properties.headcount - (snapshot.ENROLLING + snapshot.ENROLLED)
      : enrolmentSnapshot.new) || 0;
  return snapshot;
};

export const convertBackendStatusToDisplayStatus = employee => {
  if (showLeaversAsDisabled.value && employee.current_state === 'DISABLED') {
    if (employee.properties?.optout_initiated_at && !employee.properties?.optout_completed_at) {
      return 'OPT OUT PENDING';
    } else if (employee.properties?.unenrolment_initiated_at && !employee.properties?.unenrolment_completed_at) {
      return 'LEAVER';
    } else {
      return 'DISABLED';
    }
  }

  if (employee.current_state === 'DISABLED' && employee.properties?.optout_initiated_at) {
    if (!employee.properties?.optout_completed_at) {
      return 'OPT OUT PENDING';
    } else if (employee.terminated_at) {
      return 'LEAVER';
    } else {
      return 'OPTED OUT';
    }
  }

  if (employee.current_state === 'DISABLED' && employee.terminated_at) {
    return 'LEAVER';
  }

  if (employee.properties?.paused && employee.current_state !== 'DISABLED') {
    return 'PAUSED';
  }

  if (employee.properties?.pause_scheduled_at && employee.current_state !== 'DISABLED') {
    return 'PAUSE SCHEDULED';
  }

  if (employee.properties?.transfers_disabled && employee.current_state !== 'DISABLED') {
    return 'TRANSFERS DISABLED';
  }

  const convertedStatuses = {
    // the employee has not yet signed up, nothing happening yet, NOT YET ACTIVE
    NEW: 'NOT ENROLLED',
    INSTALLED: 'NOT ENROLLED',
    // the employee uses the app, USING
    ACTIVE: 'ENROLLED',
    ENROLLED: 'ENROLLED',
    // the employee is in the process of activating and using the app, ONBOARDING
    ENROLLING: 'ENROLLING',
    PENDING: 'ENROLLING',
    // for some reason the employee is not using the app, DISABLED
    FURLOUGH: 'DISABLED',
    OPTOUT: 'OPTED OUT',
    OPTOUTPENDING: 'DISABLED',
    PAUSED: 'PAUSED',
    DISABLED: 'DISABLED',
  };

  return convertedStatuses[employee.current_state] || 'NO STATUS';
};

const removeBeneficiaryDetails = employee => {
  const employeeNoBanking = cloneDeep(employee);
  delete employeeNoBanking.properties.beneficiary_account_number;
  delete employeeNoBanking.properties.beneficiary_sort_code;
  return employeeNoBanking;
};

export const updateEmployeeAndBanking = async employee => {
  // beneficiiary details need to be removed from the employee object before patching, since the
  // banking patch does a diff between the payload and the current state of the employee
  // so if we have just updated the bank details, the banking update will always be a no-op
  const employeeNoBanking = removeBeneficiaryDetails(employee);

  // if we're usng the external id as the employee code, we should never try and patch the employee code
  if (usingExternalIdAsEmployeeCode.value) {
    delete employeeNoBanking.employee_code;
  }

  await ApiClient.updateEmployee(employeeNoBanking);
  await ApiClient.patchSmeEmployeeBanking(
    employee.employee_id,
    employee.properties.beneficiary_account_number,
    employee.properties.beneficiary_sort_code,
    employee.properties.beneficiary_type,
  );
};
